/**
 * Internationalization (i18n) System for Event Finder
 * Supports: US English, Canadian English, Canadian French, Latin American Spanish, Brazilian Portuguese
 */

// Import all translations
import { enUS } from './en-US';
import { enCA } from './en-CA';
import { frCA } from './fr-CA';
import { es419 } from './es-419';
import { ptBR } from './pt-BR';

// Supported locales
export type Locale = 'en-US' | 'en-CA' | 'fr-CA' | 'es-419' | 'pt-BR';

// Translation interface structure
export interface Translations {
  // App branding
  app: {
    name: string;
    tagline: string;
    description: string;
  };

  // Header/Navigation
  header: {
    searchPlaceholder: string;
    searchingEvents: string;
    noSuggestionsFound: string;
    searchButton: string;
    categoriesView: string;
    mapView: string;
    calendarView: string;
    feedback: string;
    createGroup: string;
    myEvents: string;
    menu: string;
    howItWorks: string;
    latestUpdates: string;
    upcomingShort: string;
  };

  // Search suggestions
  suggestions: {
    locations: string;
    entities: string;
    events: string;
    location: string;
    relevance: string;
  };

  // Homepage
  homepage: {
    heroTitle: string;
    heroSubtitle: string;
    popularEvents: string;
    topCities: string;
    viewAllEvents: string;
    exploreEvents: string;
    discoverEvents: string;
    findEventsNearby: string;
    liveStats: {
      ariaLabel: string;
      upcoming: string;
      inPerson: string;
      virtual: string;
      hybrid: string;
      organizations: string;
      partners: string;
      companies: string;
      webSources: string;
      cities: string;
      countries: string;
      continents: string;
      addedPastWeek: string;
      updated: string;
      fresh: string;
      loading: string;
    };
  };

  // Events
  events: {
    upcoming: string;
    past: string;
    virtual: string;
    inPerson: string;
    free: string;
    paid: string;
    eventDetails: string;
    venue: string;
    date: string;
    time: string;
    location: string;
    organizer: string;
    category: string;
    register: string;
    moreInfo: string;
    noEventsFound: string;
    loadingEvents: string;
    relatedTo: string;
    duration: string;
    eventSource: string;
    universityHosted: string;
    universityFallbackName: string;
    universityAttendanceNotice: string;
    universityCampusPrecision: string;
  };

  // Browse page
  browse: {
    title: string;
    searchResultsTitle: string;
    searchResultsDescription: string;
    noResultsTitle: string;
    noResultsDescription: string;
    showingResults: string;
    filtersApplied: string;
  };

  // Map page
  map: {
    title: string;
    subtitle: string;
    eventsNearby: string;
    showingEvents: string;
    zoomToArea: string;
    currentLocation: string;
    enableLocation: string;
    failedToLoadEvents: string;
    failedToGetLocation: string;
  };

  // Calendar page
  calendar: {
    title: string;
    subtitle: string;
    monthView: string;
    weekView: string;
    dayView: string;
    today: string;
    nextMonth: string;
    previousMonth: string;
    eventsToday: string;
    noEventsThisDay: string;
  };

  // Filters
  filters: {
    filterBy: string;
    dateRange: string;
    location: string;
    category: string;
    price: string;
    eventType: string;
    clearFilters: string;
    applyFilters: string;
    startDate: string;
    endDate: string;
    anyPrice: string;
    freeOnly: string;
    paidOnly: string;
    anyType: string;
    virtualOnly: string;
    inPersonOnly: string;
  };

  // Common actions
  actions: {
    search: string;
    clear: string;
    cancel: string;
    save: string;
    edit: string;
    delete: string;
    share: string;
    export: string;
    print: string;
    refresh: string;
    back: string;
    next: string;
    previous: string;
    close: string;
    submit: string;
  };

  // Time and dates
  time: {
    today: string;
    tomorrow: string;
    yesterday: string;
    thisWeek: string;
    nextWeek: string;
    thisMonth: string;
    nextMonth: string;
    morning: string;
    afternoon: string;
    evening: string;
    night: string;
    days: {
      monday: string;
      tuesday: string;
      wednesday: string;
      thursday: string;
      friday: string;
      saturday: string;
      sunday: string;
    };
    months: {
      january: string;
      february: string;
      march: string;
      april: string;
      may: string;
      june: string;
      july: string;
      august: string;
      september: string;
      october: string;
      november: string;
      december: string;
    };
  };

  // Error messages
  errors: {
    general: string;
    networkError: string;
    notFound: string;
    unauthorized: string;
    serverError: string;
    invalidInput: string;
    tryAgainLater: string;
    pageNotFound: string;
    locationError: string;
  };

  // Success messages
  success: {
    eventSaved: string;
    filtersSaved: string;
    settingsUpdated: string;
    feedbackSubmitted: string;
  };

  // Loading states
  loading: {
    general: string;
    events: string;
    suggestions: string;
    map: string;
    calendar: string;
    searching: string;
  };

  // Event categories (common ones)
  categories: {
    technology: string;
    business: string;
    education: string;
    healthcare: string;
    entertainment: string;
    sports: string;
    arts: string;
    science: string;
    community: string;
    networking: string;
    conference: string;
    workshop: string;
    seminar: string;
    meetup: string;
    webinar: string;
  };

  // How It Works page
  howItWorks: {
    title: string;
    subtitle: string;
    heroTitle: string;
    heroSubtitle: string;
    
    // Challenge section
    challengeTitle: string;
    challengeDescription: string;
    
    // Solution section
    solutionTitle: string;
    solutionDescription: string;
    
    // AI Vision System
    aiVisionTitle: string;
    aiVisionDescription: string;
    
    // Primary Method
    primaryMethodTitle: string;
    primaryMethodSubtitle: string;
    primaryMethodDescription: string;
    primaryMethodFeatures: {
      webScraping: string;
      gptProcessing: string;
      contextUnderstanding: string;
    };
    
    // Fallback Method
    fallbackMethodTitle: string;
    fallbackMethodSubtitle: string;
    fallbackMethodDescription: string;
    fallbackMethodFeatures: {
      screenshotTechnology: string;
      computerVision: string;
      layoutIntelligence: string;
    };
    
    // Smart Matching
    smartMatchingTitle: string;
    smartMatchingDescription: string;
    
    // Vector Similarity
    vectorSimilarityTitle: string;
    vectorSimilarityFeatures: {
      semanticUnderstanding: string;
      intelligentDeduplication: string;
      confidenceScoring: string;
    };
    
    // Fuzzy Logic
    fuzzyLogicTitle: string;
    fuzzyLogicFeatures: {
      dateIntelligence: string;
      locationMatching: string;
      titleVariations: string;
    };
    
    // Processing Pipeline
    processingPipelineTitle: string;
    processingPipelineDescription: string;
    processingPipelineFeatures: {
      qualityControl: string;
      freshnessFiltering: string;
      dataValidation: string;
      errorRecovery: string;
    };
    
    // Cloud Architecture
    cloudArchitectureTitle: string;
    cloudArchitectureDescription: string;
    cloudArchitectureFeatures: {
      parallelProcessing: string;
      autoScaling: string;
      faultTolerance: string;
      globalReach: string;
    };
    
    // Results section
    resultsTitle: string;
    resultsDescription: string;
    
    // Business Value
    businessValueTitle: string;
    businessValueForBD: string;
    businessValueForMarketing: string;
    businessValueForSales: string;
    
    // Technology Stack
    technologyStackTitle: string;
    azureOpenAITitle: string;
    azureSearchTitle: string;
    analyticsTitle: string;
    
    // Real World Impact
    realWorldImpactTitle: string;
    realWorldImpactDescription: string;
    
    // CTA
    ctaTitle: string;
    ctaDescription: string;
    ctaButton: string;
  };

  // Footer
  footer: {
    companyName: string;
    companyDescription: string;
    contactEmail: string;
    contactPhone: string;
    contactAddress: string;
    
    // CTA Section
    ctaTitle: string;
    ctaDescription: string;
    ctaButton: string;
    
    // Quick Links Section
    quickLinksTitle: string;
    aboutUs: string;
    ourServices: string;
    blogResources: string;
    latestUpdates: string;
    careers: string;
    helpCenter: string;
    contact: string;
    
    // Platform Section
    platformTitle: string;
    browseEvents: string;
    eventCalendar: string;
    advancedSearch: string;
    apiDocumentation: string;
    mobileApp: string;
    siteMap: string;
    siteStatistics: string;
    
    // Trust & Safety Section
    trustSafetyTitle: string;
    securityCenter: string;
    accessibility: string;
    communityGuidelines: string;
    trustSafety: string;
    reportIssue: string;
    
    // Trust Badges
    sslSecured: string;
    iso27001: string;
    gdprCompliant: string;
    
    // Legal Links
    privacyPolicy: string;
    termsOfService: string;
    cookiePolicy: string;
    disclaimer: string;
    
    // Copyright
    copyright: string;
    allRightsReserved: string;
  };

  // Regional specific
  regional: {
    currency: string;
    dateFormat: string;
    timeFormat: string;
    numberFormat: string;
    phoneFormat: string;
    addressFormat: string;
  };
}

// Default locale
export const DEFAULT_LOCALE: Locale = 'en-US';

// Detect user's preferred locale
export const detectUserLocale = (): Locale => {
  // Check localStorage first
  const storedLocale = localStorage.getItem('event-finder-locale') as Locale;
  if (storedLocale && isValidLocale(storedLocale)) {
    return storedLocale;
  }

  // Check browser language
  const browserLang = navigator.language || navigator.languages?.[0];
  
  if (browserLang) {
    // Map browser languages to our supported locales
    if (browserLang.startsWith('en-CA')) return 'en-CA';
    if (browserLang.startsWith('fr-CA') || (browserLang.startsWith('fr') && navigator.language.includes('CA'))) return 'fr-CA';
    if (browserLang.startsWith('es')) {
      // Detect Latin American Spanish vs Spain Spanish
      const region = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (region && (region.includes('America') || region.includes('Mexico'))) {
        return 'es-419';
      }
    }
    if (browserLang.startsWith('pt-BR') || (browserLang.startsWith('pt') && navigator.language.includes('BR'))) return 'pt-BR';
    if (browserLang.startsWith('en')) return 'en-US';
  }

  return DEFAULT_LOCALE;
};

// Validate locale
export const isValidLocale = (locale: string): locale is Locale => {
  return ['en-US', 'en-CA', 'fr-CA', 'es-419', 'pt-BR'].includes(locale);
};

// Store user's locale preference
export const setUserLocale = (locale: Locale): void => {
  localStorage.setItem('event-finder-locale', locale);
};

// Get locale display name
export const getLocaleDisplayName = (locale: Locale): string => {
  const names: Record<Locale, string> = {
    'en-US': 'English (United States)',
    'en-CA': 'English (Canada)',
    'fr-CA': 'Français (Canada)',
    'es-419': 'Español (Latinoamérica)',
    'pt-BR': 'Português (Brasil)',
  };
  return names[locale];
};

// Regional formatting utilities
export const formatDate = (date: Date, locale: Locale): string => {
  const formats: Record<Locale, Intl.DateTimeFormatOptions> = {
    'en-US': { month: 'long', day: 'numeric', year: 'numeric' },
    'en-CA': { day: 'numeric', month: 'long', year: 'numeric' },
    'fr-CA': { day: 'numeric', month: 'long', year: 'numeric' },
    'es-419': { day: 'numeric', month: 'long', year: 'numeric' },
    'pt-BR': { day: 'numeric', month: 'long', year: 'numeric' },
  };
  
  return new Intl.DateTimeFormat(locale, formats[locale]).format(date);
};

export const formatTime = (date: Date, locale: Locale): string => {
  const is24Hour = locale === 'fr-CA' || locale === 'pt-BR';
  
  return new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: !is24Hour,
  }).format(date);
};

export const formatCurrency = (amount: number, locale: Locale): string => {
  const currencies: Record<Locale, string> = {
    'en-US': 'USD',
    'en-CA': 'CAD',
    'fr-CA': 'CAD',
    'es-419': 'USD', // Varies by country, using USD as default
    'pt-BR': 'BRL',
  };
  
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencies[locale],
  }).format(amount);
};

// Translation collection
export const translations: Record<Locale, Translations> = {
  'en-US': enUS,
  'en-CA': enCA,
  'fr-CA': frCA,
  'es-419': es419,
  'pt-BR': ptBR,
};

// Get translations for a specific locale
export const getTranslations = (locale: Locale): Translations => {
  return translations[locale] || translations['en-US'];
};

// Hook-ready translation getter
export const createTranslationGetter = (locale: Locale) => {
  const t = getTranslations(locale);
  
  return {
    t: (key: string) => {
      const keys = key.split('.');
      let value: any = t;
      
      for (const k of keys) {
        value = value?.[k];
      }
      
      return value || key;
    },
    locale,
    translations: t,
  };
};
